import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from "recharts";
import { useTheme } from "@mui/material";

const SaleCharts = ({ DailyData, lastweekDay }) => {
  const theme = useTheme();
  const dayinweek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  // --- 1. Determine Day Names ---
  
  // NOTE: Assuming startTime is available on the first order, we use that date to get the day name.
  // If DailyData is empty, we default to today's actual day.
  const referenceDate = DailyData[0]?.startTime ? new Date(DailyData[0].startTime) : new Date();
  
  const dayIndex = referenceDate.getDay();
  const todayDayName = dayinweek[dayIndex];
  
  // Names for the legend and tooltip
  const currentLabel = `${todayDayName} (Current)`;
  const previousLabel = `Last ${todayDayName}`;


  // --- 2. Data Processing & Combination ---
  const hourlyData = useMemo(() => {
    // 2a. Initialize 24 hours with 0 sales for both periods
    const hoursMap = Array.from({ length: 24 }, (_, i) => ({
      hour: `${i.toString().padStart(2, "0")}:00`,
      currentSale: 0,
      lastWeekSale: 0
    }));

    // 2b. Process Current Day Data
    DailyData.forEach(order => {
      if (!order.startTime || !order.total) return;

      const orderDate = new Date(order.startTime);
      const hour = orderDate.getHours();
      
      if (hoursMap[hour]) {
          hoursMap[hour].currentSale += order.total;
      }
    });

    // 2c. Process Last Week's Data
    lastweekDay.forEach(order => {
      if (!order.startTime || !order.total) return;

      const orderDate = new Date(order.startTime);
      const hour = orderDate.getHours();
      
      if (hoursMap[hour]) {
          hoursMap[hour].lastWeekSale += order.total;
      }
    });
    
    // 2d. Final Formatting and filtering out 0 sales across both periods
    return hoursMap
        .filter(h => h.currentSale > 0 || h.lastWeekSale > 0)
        .map(h => ({
            ...h,
            // Format numbers to 2 decimal places for cleaner display
            currentSale: parseFloat(h.currentSale.toFixed(2)),
            lastWeekSale: parseFloat(h.lastWeekSale.toFixed(2)),
        }));

  }, [DailyData, lastweekDay]);

  return (
    <ResponsiveContainer width="100%" height={300}>
      <ComposedChart data={hourlyData}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="hour"
          label={{ value: "Hour of Day", position: "insideBottom", offset: -5 }}
        />
        <YAxis label={{ value: "Sales ($)", angle: -90, position: "insideLeft" }} />
        <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
        <Legend />
        
        {/* Current Day Sales (Bar Chart for prominence) */}
        <Bar dataKey="currentSale" fill={theme.palette.primary.main} name={currentLabel} />
        
        {/* Last Week's Sales (Line Chart for comparison trend) */}
        <Line 
            type="monotone" 
            dataKey="lastWeekSale" 
            stroke={theme.palette.error.main} // Use a contrasting color for the comparison line
            name={previousLabel} 
            strokeWidth={2}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export default SaleCharts;